import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../../firebase";
import { IconSearch } from "@tabler/icons-react";

type Visitor = {
	id: string;
	createdAt: string;
	lastVisit: string;
	visitCount: number;
	language: string;
	success: boolean;
	display_name?: string;
	address?: {
		city?: string;
		country?: string;
	};
};

export default function VisitorsPage() {
	const [visitors, setVisitors] = useState<Visitor[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		const fetchVisitors = async () => {
			try {
				const querySnapshot = await getDocs(collection(firestore, "visiters"));
				const visitorsData = querySnapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				})) as Visitor[];
				setVisitors(visitorsData);
			} catch (error) {
				console.error("Error fetching visitors:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchVisitors();
	}, []);

	const filteredVisitors = visitors.filter(
		(visitor) =>
			visitor.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
			visitor.language.toLowerCase().includes(searchTerm.toLowerCase()) ||
			visitor.address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			visitor.address?.country?.toLowerCase().includes(searchTerm.toLowerCase())
	);

	if (loading) {
		return (
			<div className='flex h-[calc(100vh-4rem)] items-center justify-center'>
				<div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
			</div>
		);
	}

	return (
		<div>
			<div className='mb-8 sm:flex sm:items-center sm:justify-between'>
				<div>
					<h1 className='text-2xl font-semibold text-gray-900 dark:text-white'>Visitors</h1>
					<p className='mt-2 text-sm text-gray-700 dark:text-gray-400'>
						A list of all visitors who have accessed the website
					</p>
				</div>
				<div className='mt-4 sm:mt-0'>
					<div className='relative'>
						<div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
							<IconSearch className='h-5 w-5 text-gray-400' />
						</div>
						<input
							type='text'
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							placeholder='Search visitors...'
							className='block w-full rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 py-2.5 pl-10 pr-3 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:focus:border-primary'
						/>
					</div>
				</div>
			</div>

			<div className='mt-4 flow-root'>
				<div className='-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
					<div className='inline-block min-w-full py-2 align-middle'>
						<div className='overflow-hidden shadow ring-1 ring-black ring-opacity-5 dark:ring-white/10 sm:rounded-lg'>
							<table className='min-w-full divide-y divide-gray-200 dark:divide-gray-800'>
								<thead className='bg-gray-50 dark:bg-gray-800/50'>
									<tr>
										<th className='py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 dark:text-gray-200 sm:pl-6'>
											Device ID
										</th>
										<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
											Location
										</th>
										<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
											Language
										</th>
										<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
											Visit Count
										</th>
										<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
											First Visit
										</th>
										<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
											Last Visit
										</th>
									</tr>
								</thead>
								<tbody className='divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900'>
									{filteredVisitors.map((visitor) => (
										<tr key={visitor.id} className='hover:bg-gray-50 dark:hover:bg-gray-800/50'>
											<td className='whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100 sm:pl-6'>
												{visitor.id}
											</td>
											<td className='whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400'>
												{visitor.address
													? `${visitor.address.city || ""}, ${visitor.address.country || ""}`
													: "Unknown"}
											</td>
											<td className='whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400'>
												{visitor.language}
											</td>
											<td className='whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400'>
												{visitor.visitCount}
											</td>
											<td className='whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400'>
												{new Date(visitor.createdAt).toLocaleDateString()}
											</td>
											<td className='whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400'>
												{new Date(visitor.lastVisit).toLocaleDateString()}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
